import Grid2 from '@mui/material/Unstable_Grid2';
import { useRouter } from 'next/router';
import { useEffect, useId, useState } from 'react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/pagination';

import Dropdown from '@/components/BaseLibrary/Dropdown/Dropdown';
import ArticleHeroImage from '@/components/BlogComponents/shared/ArticleHeroImage/ArticleHeroImage';
import Icon, { IconName } from '@/components/Icon/Icon';
import GridContainer2 from '@/components/Layout/GridContainer2/GridContainer2';
import Link from '@/components/Link/Link';
import { SpacingContainer } from '@/components/SpacingContainer/SpacingContainer';
import type { BlogPageType } from '@/contentful/api/types';
import useGlobalStyles from '@/styles/global.styles';
import type { BlogNavItemCf } from '@/types';

import useStyles from './BlogHubHero.styles';
import CategoryPill from './CategoryPill';

export default function BlogHubHero({
  categoryNav,
  category,
  allCategoriesUrl,
  featuredArticles,
  eyebrow,
  heading,
  subheading,
}: {
  category?: string;
  categoryNav?: BlogNavItemCf[];
  allCategoriesUrl?: string;
  featuredArticles: BlogPageType[];
  eyebrow?: string;
  heading?: string;
  subheading?: string;
}): React.JSX.Element {
  const router = useRouter();
  const [selectedCategory, setSelectedCategory] = useState('All Categories');

  useEffect(() => {
    setSelectedCategory(category ?? 'All Categories');
  }, [category]);

  const { classes, cx } = useStyles({ isOnDark: false });
  const { classes: globalClasses } = useGlobalStyles({ fixed: true });
  const prevElId = useId().replaceAll(':', '');
  const nextElId = useId().replaceAll(':', '');
  const paginationId = useId().replaceAll(':', '');
  const categoryData: { [key: string]: string } = {};

  if (categoryNav) {
    if (allCategoriesUrl) {
      categoryData['All Categories'] = allCategoriesUrl;
    }

    categoryNav?.forEach((navItem: BlogNavItemCf) => {
      const navItemInfo = navItem.fields;
      const categoryPageUrl = navItemInfo.categoryPageUrl;

      const category = navItemInfo.category;
      const categoryName = category?.fields.categoryName;
      if (categoryName) {
        categoryData[categoryName] = categoryPageUrl;
      }
    });
  }

  return (
    <SpacingContainer isFeatured className={classes.blogHubHero}>
      <GridContainer2
        fixed
        gridProps={{ className: classes.blogHubHeroGridContainer }}
      >
        <Grid2 className={classes.blogHubHeroHeader}>
          {eyebrow && (
            <p className={cx(globalClasses.body2, classes.blogHubEyebrow)}>
              {eyebrow}
            </p>
          )}
          {heading && <h1 className={globalClasses.h1}>{heading}</h1>}
          {subheading && <p className={globalClasses.quote}>{subheading}</p>}
        </Grid2>

        {/* Mobile View */}
        <Grid2 className={classes.blogHubHeroCategoriesDropdownContainer}>
          <Dropdown
            onChange={(e) => {
              router.push(categoryData[e]);
            }}
            options={[...Object.keys(categoryData)]}
            defaultOption={selectedCategory}
            label='Category'
            hideLabel={true}
          />
        </Grid2>

        {/* Desktop View */}
        <Grid2
          className={classes.blogHubHeroCategories}
          data-testid='categoryNav'
        >
          {Object.entries(categoryData)?.map(([key, value], idx) => (
            <Link
              key={`${key}-${
                // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                idx
              }`}
              href={value}
            >
              <CategoryPill label={key} active={key === selectedCategory} />
            </Link>
          ))}
        </Grid2>

        <Grid2 className={classes.blogHubHeroFeaturedArticles}>
          <Swiper
            spaceBetween={50}
            navigation={{
              prevEl: `#${prevElId}`,
              nextEl: `#${nextElId}`,
              disabledClass: classes.navigationButtonDisabled,
            }}
            modules={[Navigation, Pagination, Autoplay]}
            slidesPerView={1}
            pagination={{
              el: `#${paginationId}`,
              clickable: true,
              bulletActiveClass: 'bullet-active',
              bulletClass: 'bullet',
              horizontalClass: classes.horizontal,
              // biome-ignore lint/correctness/noUnusedFunctionParameters: Automated migration to Biome
              renderBullet: (index: number, className: string) => {
                return `<svg class="${className}" width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="4" cy="4" r="4" fill="currentColor"/>
              </svg>`;
              },
            }}
            autoplay={{ delay: 4000, disableOnInteraction: true }}
          >
            {featuredArticles?.map((article, idx) => {
              return (
                <SwiperSlide
                  key={`${article.blogTitle}-${idx}`}
                  className={classes.swiperSlide}
                >
                  <ArticleHeroImage article={article} isHero />
                </SwiperSlide>
              );
            })}
          </Swiper>
          <div className={classes.navigation}>
            <button
              id={prevElId}
              type='button'
              className={classes.navigationButton}
            >
              <Icon name={IconName.ARROW_LEFT} width={24} />
            </button>
            <div id={paginationId} className={classes.pagination}></div>
            <button
              id={nextElId}
              type='button'
              className={classes.navigationButton}
            >
              <Icon name={IconName.ARROW_RIGHT} width={24} />
            </button>
          </div>
        </Grid2>
      </GridContainer2>
    </SpacingContainer>
  );
}
